<template>
    <div :id="rootElementId" :style="`height:${PITCH_HEIGHT}px`"></div>
</template>

<script>

import { getDataUrl, getNeutralColors, isWhite } from '../../utils/functions';
import { loadFonts,copyAndAppendElement } from '../../utils/exportToPNG';
import ResizeObserver from 'resize-observer-polyfill';
import Dictionary from '../../dictionary';

  export default {
    name: 'XGGoalChart',
    props: {
      id:{
        type: String,
        required: true
      },
      size: {
        type: Number,
        required: false,
      },
      margin: {
        type: Object,
        required: false,
      },
      shots: {
        type: Array,
        required: true,
      },
      game: {
        type: Object,
        required: false
      },
      playerSeasonData: {
        type: Array,
        required: false
      },
      circlesAndBoxes: {
        type: Array,
        required: false
      },
      playerName: {
        type: String,
        required: false
      },
      team:{
        type: Boolean,
        default: false
      },
      playerPosition: {
        type: Number,
        required: false
      },
      language: {
        type: String,
        required: true
      },
      homeFillColor:{
        type: String,
        default: "#ffffff"
      },
      homeBorderColor:{
        type: String,
        default: "#000000"
      },
      awayFillColor:{
        type: String,
        default: "#ffffff"
      },
      awayBorderColor:{
        type: String,
        default: "#000000"
      },
      mediaMode:{
        type: Boolean,
        default: false
      },
      postGameAnalysis:{
        type: Boolean,
        default: false
      },
      xGOnTarget:{
        type: Boolean,
        default: false
      },
      finishingXGOT:{
        type: Boolean,
        default: false
      },
      //gameId for print one game Player xG Map
      gameId:{
        type:Number,
        default: 0
      },
      //gameIds for print number of games selected
      gameIds: {
        type: Array,
        required: false,
        default: () => []
      },
      gameName:{
        type: String,
        default: ""
      },
      fontFamily:{
        type: String,
        default: 'NeueMontreal'
      },
      minsPlayed: {
        type: Number,
        default: 0,
      },
      showPenalties: {
        type: Boolean,
        default: false
      },
      gamesDisplayText: {
        type: String,
        default: ''
      },
      teamOrOpponent: {
        type: Boolean,
        default: true
      },
      teamId: {
        type: Number,
        default: 0
      },
      filterShotTypeText: {
        type: String,
        default: ''
      },
      filterShotStrategyText: {
        type: String,
        default: ''
      },
      filterBodyPartText: {
        type: String,
        default: ''
      },
      filterMinutesText: {
        type: String,
        default: ''
      },
      filterMinutesRange: {
        type: String,
        default: ''
      },
    },
    computed: {
      SVG_WIDTH(){
        return this.size ? this.size : 380;
      },
      PITCH_WIDTH(){
        return this.SVG_WIDTH;
      },
      PITCH_HEIGHT() {
        return this.SVG_WIDTH / 1.93;
      },
      rootElementId(){
        return `pitchXG${this.id}`
      },
      currentUserFieldColor() {
        return this.$store.getters["auth/fieldColor"];
      },
      totalPenalties() { 
        return this.shots.filter(d => d.penaltyGoal).length;
      },
      totalPenaltyGoals() {
        return this.shots.filter(d => d.penaltyGoal && d.type === "GOAL").length;
      },
      shotsOutOfRange() {
        return this.shots.filter(d => !(d.y1 < 61 && d.y1 > 39 && d.z_mouth < 92.5));
      }
    },
    data() {
      return {
        height: 0,
        width: 0,
        images: {},
        observer: null,
      }
    },

    mounted() {
      this.observer = new ResizeObserver(this.onResize).observe(document.getElementById(this.rootElementId));
      this.printD3();
    },
    methods: {
      async printD3(force = false){
        
        if(force){
          this.images = {};
        }

        const SVG_WIDTH = this.SVG_WIDTH;
        const margin = this.margin ? this.margin : {top: 0, right: 0, bottom: 0, left: 0};

        const PITCH_WIDTH = this.PITCH_WIDTH;
        const PITCH_HEIGHT = this.PITCH_HEIGHT;
        //Only shots saved by GK
        let shotsBlockedGK = this.shots.filter(s => !s.blockedByOutfielderPlayer);
        //Filter penalty and ownGoals and shots outside de chart
        let filteredShots = this.showPenalties ? shotsBlockedGK.filter(p => !p.ownGoal) : shotsBlockedGK.filter(p => !p.penaltyGoal).filter(p => !p.ownGoal);
       
        d3.selectAll('.tooltip-no-arrow').remove();

        d3.select(`#${this.rootElementId}`).select("svg").remove();

        const tooltip = d3
          .select("body")
          .append("div")
          .attr("display", "none")
          .attr("class", "tooltip-no-arrow tooltip-no-arrow--centered");

        var svg = d3.select(`#${this.rootElementId}`)
          .append("svg")
          .attr("width", SVG_WIDTH)
          .attr("height", PITCH_HEIGHT + margin.top + margin.bottom)
          .style("background-color", "#ffffff")
          .attr("id",`${this.rootElementId}SVG`)
          .attr("class", "pitch");

        this.drawImageBase64(svg, 0, 0, PITCH_WIDTH, PITCH_HEIGHT, require(`@/assets/img/front_goal.png`),"pitch",true);

        let neutralColorsHome = getNeutralColors('home');
        let firstHomeTeamColor = this.homeFillColor;
        let secondHomeTeamColor = this.homeBorderColor;
        let neutralColorsAway = getNeutralColors('away');
        let firstAwayTeamColor =  this.awayFillColor;
        let secondAwayTeamColor = this.awayBorderColor;
        //If is game xG Map but it's post game, don't get team colors, keep neutral colors
        if (this.game && !this.mediaMode && !this.postGameAnalysis) {
          firstHomeTeamColor = this.game.homeTeamGameColor ? this.game.homeTeamGameColor : (this.game.homeTeamColor ? this.game.homeTeamColor : firstHomeTeamColor);
          secondHomeTeamColor = this.game.homeTeamGameSecondColor ? this.game.homeTeamGameSecondColor : (this.game.homeTeamSecondColor ? this.game.homeTeamSecondColor : secondHomeTeamColor);
          firstAwayTeamColor = this.game.awayTeamGameColor ? this.game.awayTeamGameColor : (this.game.awayTeamColor ? this.game.awayTeamColor : firstAwayTeamColor);
          secondAwayTeamColor = this.game.awayTeamGameSecondColor ? this.game.awayTeamGameSecondColor : (this.game.awayTeamSecondColor ? this.game.awayTeamSecondColor : secondAwayTeamColor);
        }
        if(this.game && firstHomeTeamColor === firstAwayTeamColor){
          firstAwayTeamColor = neutralColorsAway[0];
        }
        if(this.game && secondHomeTeamColor === secondAwayTeamColor){
          secondAwayTeamColor = neutralColorsAway[1];
        }

        if (this.circlesAndBoxes && this.circlesAndBoxes.includes("boxes"))
          this.renderBoxes(svg,
            SVG_WIDTH,
            PITCH_HEIGHT,
            margin,
            filteredShots,
            this.language
          )

        if (this.circlesAndBoxes && this.circlesAndBoxes.includes("circles"))
          this.renderShots(svg,
            PITCH_WIDTH,
            PITCH_HEIGHT,
            margin,
            this.rootElementId,
            filteredShots,
            this.xGOnTarget,
            this.playerPosition ? this.playerPosition : "none",
            firstHomeTeamColor.replace("0x", "#"),
            secondHomeTeamColor.replace("0x", "#"),
            firstAwayTeamColor.replace("0x", "#"),
            secondAwayTeamColor.replace("0x", "#"),
            tooltip,
            this.game ? true : false);
       
        this.printPlayerXGLegend(svg, PITCH_WIDTH, margin, filteredShots, this.xGOnTarget, this.finishingXGOT, this.playerSeasonData, this.playerName, this.playerPosition ? this.playerPosition : "none", this.language);

        // Draw Driblab Logo (original position)
        this.drawImageBase64(svg, PITCH_WIDTH - (PITCH_WIDTH * 0.2),
        PITCH_HEIGHT - (PITCH_HEIGHT * 0.18),
        PITCH_WIDTH * 0.18, PITCH_HEIGHT * 0.18,
        require(`@/assets/img/driblab${this.mediaMode ? "_normal" : ""}_white.png`),'logoDriblab');

        let font = await loadFonts(this.fontFamily);
        copyAndAppendElement(svg.node(),font.node(),0,0);

        // Wrap footnote text within the area left of the player info rect, then stack blocks pushing later ones down when earlier ones wrap.
        const baseFootnoteFontSize = 0.0135 * PITCH_WIDTH;
        const wrappedLineHeight = baseFootnoteFontSize * 1.3;
        const interBlockStep = PITCH_HEIGHT * 0.041;
        const legendScale = 0.7;
        const legendRectW = 0.72 * PITCH_WIDTH * legendScale;
        const legendRectX = (PITCH_WIDTH - legendRectW) / 2 + margin.left;
        const footnoteGap = PITCH_WIDTH * 0.008;
        const wrapFootnote = (text, startX) => {
          const maxW = Math.max(0, legendRectX - startX - footnoteGap);
          if (maxW <= 0 || this.measureText(text, baseFootnoteFontSize) <= maxW) return [text];
          const words = text.split(' ');
          const lines = [];
          let current = '';
          for (const word of words) {
            const candidate = current ? current + ' ' + word : word;
            if (!current || this.measureText(candidate, baseFootnoteFontSize) <= maxW) {
              current = candidate;
            } else {
              lines.push(current);
              current = word;
            }
          }
          if (current) lines.push(current);
          return lines;
        };

        const footnoteBlocks = [];
        if (this.shotsOutOfRange.length) {
          const x = PITCH_WIDTH * 0.025;
          const raw = `${ Dictionary[this.language]["finishingMap"]["chart"]["exceptionShotsOutOfRange"] } (${this.shotsOutOfRange.length})`;
          footnoteBlocks.push({ x, preferredY: PITCH_HEIGHT * 0.828, lines: wrapFootnote(raw, x) });
        }
        if (this.showPenalties) {
          const x1 = PITCH_WIDTH * 0.025;
          const raw1 = `${this.playerPosition === 1 ? Dictionary[this.language]["finishingMap"]["chart"]["exceptionTextGKPenaltyGoals"] : Dictionary[this.language]["finishingMap"]["chart"]["exceptionTextPenaltyGoals"]}`;
          footnoteBlocks.push({ x: x1, preferredY: PITCH_HEIGHT * 0.869, lines: wrapFootnote(raw1, x1) });

          const x2 = PITCH_WIDTH * 0.035;
          const raw2 = `${this.totalPenaltyGoals}/${this.totalPenalties} ${ Dictionary[this.language]["team"]["overviewTab"]["stats"]["goalsPenalty"] }`;
          footnoteBlocks.push({ x: x2, preferredY: PITCH_HEIGHT * 0.91, lines: wrapFootnote(raw2, x2) });
        }

        let prevFootnoteLineY = -Infinity;
        footnoteBlocks.forEach(block => {
          const firstLineY = Math.max(block.preferredY != null ? block.preferredY : -Infinity, prevFootnoteLineY + interBlockStep);
          block.lines.forEach((line, j) => {
            this.writeText(svg, block.x, firstLineY + j * wrappedLineHeight + margin.top, "start", baseFootnoteFontSize + "px", line);
          });
          prevFootnoteLineY = firstLineY + (block.lines.length - 1) * wrappedLineHeight;
        });

        //Emit when is loaded
        this.$emit("loaded", this.id);
      },
      measureText(string, fontSize = 10) {
        const widths = [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0.278125,0.278125,0.35625,0.55625,
      		0.55625,0.890625,0.6671875,0.1921875,0.334375,0.334375,0.390625,0.584375,0.278125,0.334375,0.278125,0.303125,0.55625,
      		0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.278125,0.278125,0.5859375,0.584375,0.5859375,
      		0.55625,1.015625,0.6671875,0.6671875,0.7234375,0.7234375,0.6671875,0.6109375,0.778125,0.7234375,0.278125,0.5,0.6671875,
      		0.55625,0.834375,0.7234375,0.778125,0.6671875,0.778125,0.7234375,0.6671875,0.6109375,0.7234375,0.6671875,0.9453125,
      		0.6671875,0.6671875,0.6109375,0.278125,0.35625,0.278125,0.478125,0.55625,0.334375,0.55625,0.55625,0.5,0.55625,
      		0.55625,0.278125,0.55625,0.55625,0.2234375,0.2421875,0.5,0.2234375,0.834375,0.55625,0.55625,0.55625,0.55625,
      		0.334375,0.5,0.278125,0.55625,0.5,0.7234375,0.5,0.5,0.5,0.35625,0.2609375,0.3546875,0.590625];
        const avg = 0.5293256578947368;

        return string
          .split('')
          .map(c => c.charCodeAt(0) < widths.length ? widths[c.charCodeAt(0)] : avg)
          .reduce((cur, acc) => acc + cur) * fontSize;
      },
      drawImageBase64(svg,x,y,width,height,img,idName,bottom = false) {
        if(!this.images[idName]){
          let base_image = new Image();
          base_image.crossOrigin = "anonymous";
          base_image.src = img;
          base_image.onload = (event) => {
            const dataUrl = getDataUrl(event.currentTarget);
            this.drawImage(
            svg,
            x,
            y,
            width,
            height,
            dataUrl,
            idName,
            bottom
            );
          }
        }else{
          if(bottom){
            svg.node().insertBefore(this.images[idName],svg.node().firstChild);
          }else{
            svg.node().appendChild(this.images[idName]);
          }
        }
      },
      drawImage: function(svg, x, y, width, height, img,idName,bottom = false) {
        let imgNode = bottom ? svg.insert("svg:image",":first-child") : svg.append("svg:image");
          imgNode.attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .attr("xlink:href", img);
      this.images[idName] = imgNode.node().cloneNode(true);
      },
      drawRectangle(svg, x, y, width, height, color, opacity, className,insertLast = false) {
        let node = insertLast ? svg.insert("rect",":last-child") : svg.append("rect");
        return node
          .attr("class", className)
          .attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .style("fill", color)
          .style("opacity", opacity);
      },
      writeText(svg, x, y, anchor, fontSize, text, fill = "white", className = "none") {
        return svg.append("text")
          .attr("x", x)
          .attr("y", y)
          .attr("text-anchor", anchor)
          .attr("dy", "0.8em")
          .attr("font-weight", 500)
          .attr("class", className)
          .style("font-size", fontSize)
          .style("font-family", this.fontFamily)
          .style("fill", fill)
          .text(text);
      },
      getTypeOfShot(name) {
      	if (name === "SAVED SHOT") return "Saved Shot";
      	if (name === "GOAL") return "Goal";
      	if (name === "MISSED SHOT") return "Missed Shot";
      	if (name === "SHOT ON POST") return "Shot on Post";
        if (name === "MISSED CHANCE") return "Chance Missed";
        if (name === "CHANCE MISSED") return "Chance Missed";
        if (name === "Regular play") return "Regular Play";
        if (name && name.startsWith("Individual")) return "Individual Play";
        if (name === "Fast break") return "Fast Break";
        if (name === "From corner") return "From Corner";
        if (name === "Free kick") return "Free Kick";
        if (name === "Set piece") return "Set Piece";
        if (name && name.startsWith("Throw")) return "Throw In";
        if (name === "Left footed") return "Left Foot";
        if (name === "Right footed") return "Right Foot";
        if (name === "Head") return "Head";
        if (name === "Other body part") return "Other Body Part";
        if (name === "Keeper Throw") return "Keeper Throw";
        if (name === "Penalty") return "Penalty";
        console.log("Unkown " + name);
      	return "??";
      },
      renderShots(svg, pitchSize, pitchHeight, margin, elementId, xgShots, xGOnTargetOption, playerPosition, teamColor, strokeColor, awayTeamColor, awayStrokeColor, tooltip, isGameXG = true) {
        if(this.currentUserFieldColor === 'white'){
          if(isWhite(teamColor)){
            teamColor = "#464646"
          }
          if(isWhite(awayTeamColor)){
            awayTeamColor = "#464646"
          }
        }

        let x = d3.scale.linear().domain([39 , 61]).range([pitchSize, 0]);
        let y = d3.scale.linear().domain([0, 92.5]).range([pitchHeight - (pitchHeight * 0.27), 0]);

        let radius = d3.scale.linear().domain([0, 1]).range([pitchSize * 0.0055, pitchSize * 0.024]);

        var self = this;
        svg.selectAll("circle")
          .data(xgShots)
          .enter()
          .append("circle")
          .attr("r", d => playerPosition === 1 ? radius(d.xG) / 1.5 : radius(d.xG))
          .attr("cx", d => isGameXG ? d.homeTeam ? x(d.x) : x(100 - d.x) : x(d.y1))
          .attr("cy", d => isGameXG ? d.homeTeam ? y(100 - d.y) : y(d.y) : y(d.z_mouth))
          .attr('id', d => "shot" + d.id + "-" + elementId)
          .attr("class", d => "shot-" + d.playerId + " type-" + self.getTypeOfShot(d.type).split(" ").join("-") + " type-" + self.getTypeOfShot(d.strategy).split(" ").join("-") + " type-" + self.getTypeOfShot(d.bodyPart).split(" ").join("-") + " season-" + d.seasonId)
          .style("fill-opacity", (d) => self.getTypeOfShot(d.type) === "Goal" ? 0.90 : 0.25)
          .style("opacity", 0.95)
          .style("stroke", d => "black")
          .style("stroke-width", playerPosition === 1 ? pitchSize * 0.002 / 1.5 : pitchSize * 0.0015)
          .style("fill", d => self.getTypeOfShot(d.type) === "Goal" ? "green" : "gray")
          .on('mouseenter',function(xgShot) {
            d3.select(this)
              .transition()
              .duration(300)
              .style("cursor", "pointer")
              .attr('r', playerPosition === 1 ? radius(xgShot.xG) * 1.75 / 1.5 : radius(xgShot.xG) * 1.75);
            var statName = playerPosition === 1 ? 'xGOT' : 'xG';
            var shotId = document.getElementById("shot" + xgShot.id + "-" + elementId);
            var textLine1 = isGameXG ? xgShot.player : xgShot.game;
            var textLine2 = !isGameXG && playerPosition === 1 ? xgShot.player + " · " + xgShot.xG.toFixed(2) + " " + statName : xgShot.xG.toFixed(2) + " " + statName; // For GK we add shooter name to tooltip
            var textLine3 = self.getTypeOfShot(xgShot.type) + " at " + xgShot.min + ":" + (xgShot.sec < 10 ? "0" + xgShot.sec : xgShot.sec);
            var maxTextWidth = Math.max(self.measureText(textLine1, 14), self.measureText(textLine2, 14), self.measureText(textLine3, 14));
            var widthTooltip = maxTextWidth + 60;
            var height = 70;
            var topOffset = 90;
            var leftOffset = Math.ceil(shotId.getAttribute('r'));
            var tooltipTop = shotId.getBoundingClientRect().bottom + 10;
            tooltipTop = isGameXG && ((xgShot.y <= 50 && xgShot.homeTeam) || (!xgShot.homeTeam && xgShot.y >= 50)) ?
            shotId.getBoundingClientRect().top - topOffset + 10 : tooltipTop

            d3.select("body > .tooltip-no-arrow")
            .attr("font-weight", 500)
              .style("width", widthTooltip + "px")
              .style("height", height + "px")
              .style("font-size", "14px")
              .style("left", shotId.getBoundingClientRect().left - (widthTooltip/2) + leftOffset + "px")
              .style("top", (tooltipTop) + "px")
              .style("display", "inline-block")
              .style("position", "fixed")
              .html("<b>" + (isGameXG ? xgShot.player : xgShot.game )
              + "</b></br>" + ((!isGameXG && playerPosition === 1) || self.team ? xgShot.player + " · " : "")
              + "<b>" + xgShot.xG.toFixed(2)
              + " " + statName
              + (xGOnTargetOption && xgShot.xgOnTarget > 0.01 ? ' · ' + xgShot.xgOnTarget.toFixed(2) + ' xG OT' : '')
              + "</b></br>" + self.getTypeOfShot(xgShot.type)
              + " at " + xgShot.min + ":"
              + (xgShot.sec < 10 ? "0" + xgShot.sec : xgShot.sec));
          })
          .on('mouseout',function(xgShot) {
            d3.select(this)
              .transition()
              .duration(200)
              .attr('r', d => playerPosition === 1 ? radius(d.xG) / 1.5 : radius(d.xG));
            d3.select("body > .tooltip-no-arrow").style("display", "none");
          })
          .transition()
          .duration(1000)
      },
      truncateText(text, fontSize, maxWidth) {
        if (this.measureText(text, fontSize) <= maxWidth) return text;
        let t = text;
        while (this.measureText(t + '…', fontSize) > maxWidth && t.length > 1) {
          t = t.slice(0, -1);
        }
        return t + '…';
      },
      /** Final clamp for season lines: keep trailing " +N" (extra competitions) visible. */
      truncateSeasonLegendLine(line, fontSize, maxWidth) {
        const m = typeof line === 'string' && line.match(/^(.*)(\s\+\d+)$/);
        if (m && m[1].trim().length > 0) {
          const suffix = m[2];
          const mainTrim = m[1].replace(/\s+$/, '');
          const suffixW = this.measureText(suffix, fontSize);
          const maxMain = Math.max(0, maxWidth - suffixW);
          const main = this.truncateText(mainTrim, fontSize, maxMain);
          return main + suffix;
        }
        return this.truncateText(line, fontSize, maxWidth);
      },
      printPlayerXGLegend(svg, pitchWidth, margin, shots, xGOnTargetOption, finishingXGOTOption, seasons, playerName, playerPosition, language = "en") {
        // Remove Penalty and own goals to count goals and xG
        let availableShots = this.showPenalties ? shots.filter(p => !p.ownGoal || this.team) : shots.filter(p => !p.penaltyGoal).filter(p => !p.ownGoal || this.team);
        const xG = availableShots.map(shot => shot.xG).reduce((a, b) => a + b, 0);
        const xGOnTarget = availableShots.map(shot => shot.xgOnTarget).reduce((a, b) => a + b, 0);
        const finishingXGOT = availableShots.map(shot => shot.finishingXGOT).reduce((a, b) => a + b, 0);
        const shotsOnTarget = availableShots.filter(shot => shot.type === "GOAL").length + availableShots.filter(shot => shot.type === "SAVED SHOT" && !shot.blockedByOutfielderPlayer).length;
        const playerShots = availableShots.length;
        const goals = availableShots.length === 0 ? 0 : availableShots.filter(d => d.type === "GOAL").length;
        const noGamesSelected = this.gameIds && this.gameIds.length === 0;
        const games = noGamesSelected ? 0 : (this.gameIds.length ? this.gameIds.length : seasons.map(s => s.gamesPlayed).reduce((a, b) => a + b, 0));
        const minutes = noGamesSelected ? 0 : (this.minsPlayed > 0 ? this.minsPlayed : seasons.map(s => s.minutesPlayed).reduce((a, b) => a + b, 0));
        let xGDecimals = 1;

        // Scale factor to make content smaller for the goal view
        const s = 0.7;

        const rectW = 0.72 * pitchWidth * s;
        const rectH = 0.168 * pitchWidth * s;
        const rectX = (pitchWidth / 2 - rectW / 2) + margin.left;
        const rectY = this.PITCH_HEIGHT * 0.76 - pitchWidth * 0.013 * s;

        this.drawRectangle(svg, rectX, rectY, rectW, rectH, "#1a1a1a", 0.92, "xgPlayerLegendRect");

        const statName = playerPosition === 1 ? 'xGOT' : (this.teamOrOpponent ? Dictionary[this.language]["playerScout"]["xG"] : Dictionary[this.language]["statsNaming"]["xGAgainst"]);
        const goalsLabel = this.teamOrOpponent ? Dictionary[this.language]["table"]["roster"]["goals"] : Dictionary[this.language]["playerScout"]["goalsConceded"];

        const crestTeamId = this.teamId || (seasons.length > 0 ? seasons[0].teamId : 0);
        const baseCrestSize = pitchWidth * 0.054 * s;
        const nameGap = pitchWidth * 0.014 * s;
        const baseNameFontSize = 0.026 * pitchWidth * s;
        const nameWidth = this.measureText(playerName || "", baseNameFontSize);
        const adjustedNameFontSize = baseNameFontSize;

        const isConceded = !this.teamOrOpponent;
        const seasonDisplayMode = this.team ? 'one-line' : 'two-line';
        const hasSecondSeasonLine = seasonDisplayMode === 'two-line' && !(this.gameId > 0 || this.gameName) && seasons.length >= 2;
        const crestSize = hasSecondSeasonLine ? pitchWidth * 0.068 * s : baseCrestSize;

        const heroFontSizeCalc = pitchWidth * 0.025 * s;
        const goalsBaseForCalc = Dictionary[this.language]["table"]["roster"]["goals"];
        const xgBaseForCalc = Dictionary[this.language]["playerScout"]["xG"];
        let heroTextCalcForDivider = `${ goals } ${ goalsBaseForCalc }`;
        if (this.showPenalties && this.totalPenaltyGoals > 0) heroTextCalcForDivider += ` (${this.totalPenaltyGoals} P)`;
        heroTextCalcForDivider += `  ·  ${ xG.toFixed(xGDecimals) } ${ xgBaseForCalc }`;
        const heroWidthBase = this.measureText(heroTextCalcForDivider, heroFontSizeCalc);
        let heroWidthCalc = heroWidthBase;
        if (xGOnTargetOption) {
          const heroWithOt = `${heroTextCalcForDivider}  ·  ${xGOnTarget.toFixed(xGDecimals)} xG OT`;
          const heroFullW = this.measureText(heroWithOt, heroFontSizeCalc);
          const otExtra = Math.max(0, heroFullW - heroWidthBase);
          heroWidthCalc = heroWidthBase + otExtra * 0.70 + pitchWidth * 0.014 * s;
        }
        const finFontSize = pitchWidth * 0.013 * s;
        const finTextCalc = finishingXGOTOption && shotsOnTarget > 0
          ? `${ (finishingXGOT / shotsOnTarget) > 1 ? '+' : ''}${ (((finishingXGOT / shotsOnTarget) - 1) * 100).toFixed(1) }% ${ Dictionary[this.language]["statsNaming"]["Finishing"] } xG OT`
          : '';
        const finWidthCalc = finTextCalc ? this.measureText(finTextCalc, finFontSize) : 0;

        const rawNameLineW = crestSize + nameGap + nameWidth;
        const rawHeroLineW = Math.max(heroWidthCalc, finWidthCalc);
        const rawContentW = Math.max(rawNameLineW, rawHeroLineW);
        const minBoxInsetX = pitchWidth * 0.038 * s;
        const minDividerX = rectX + rectW * 0.48;
        const maxDividerX = rectX + rectW * 0.588;
        const contentToDividerGap = pitchWidth * 0.044 * s;
        const minDividerXFromInset = rectX + minBoxInsetX + rawContentW + pitchWidth * 0.028 * s;
        const dividerX = Math.min(maxDividerX, Math.max(minDividerX, rectX + rawContentW + contentToDividerGap, minDividerXFromInset));

        const leftAvailable = dividerX - rectX;
        const symmetricLeftPad = (leftAvailable - rawContentW) / 2;
        const maxLeftPad = Math.max(0, leftAvailable - rawContentW - pitchWidth * 0.010 * s);
        const targetPad = Math.max(pitchWidth * 0.056 * s, symmetricLeftPad);
        const leftPad = Math.min(maxLeftPad, Math.max(minBoxInsetX, Math.min(targetPad, maxLeftPad)));

        const leftContentH = crestSize + pitchWidth * 0.012 * s + pitchWidth * 0.030 * s + pitchWidth * 0.014 * s + (hasSecondSeasonLine ? pitchWidth * 0.010 * s : 0);
        const leftTopY = rectY + (rectH - leftContentH) / 2;
        const crestY = leftTopY;

        if (crestTeamId) {
          this.drawImageBase64(svg, rectX + leftPad, crestY, crestSize, crestSize,
            'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + crestTeamId + '.png', 'xgPlayerCrest');
        }

        const nameX = rectX + leftPad + crestSize + nameGap;
        const nameY = crestY + pitchWidth * 0.004 * s;

        svg.append("text")
          .attr("x", nameX)
          .attr("y", nameY)
          .attr("text-anchor", "start")
          .attr("dy", "0.8em")
          .attr("font-weight", 600)
          .style("font-size", adjustedNameFontSize + "px")
          .style("font-family", this.fontFamily)
          .style("fill", "white")
          .attr("class", "playerXGName")
          .text(playerName);

        const seasonFontSize = pitchWidth * 0.015 * s;
        const seasonLineH = pitchWidth * 0.016 * s;
        const seasonDividerGap = pitchWidth * 0.030 * s;
        const maxSeasonRaw = Math.max(0, dividerX - nameX - seasonDividerGap);
        const maxSeasonWidth = maxSeasonRaw * 0.92;
        let seasonLines = [];

        if (this.gameId > 0 || this.gameName) {
          seasonLines.push(this.truncateText(this.gameName || '', seasonFontSize, maxSeasonWidth));
          xGDecimals = 2;
        } else if (seasons.length > 0) {
          const names = seasons.map(s => s.seasonAlias || s.seasonName);

          if (seasonDisplayMode === 'two-line') {
            if (names.length === 1) {
              seasonLines.push(this.truncateText(names[0], seasonFontSize, maxSeasonWidth));
            } else if (names.length === 2) {
              seasonLines.push(this.truncateText(names[0], seasonFontSize, maxSeasonWidth));
              seasonLines.push(this.truncateText(names[1], seasonFontSize, maxSeasonWidth));
            } else {
              const prefix1 = names[0] + ', ';
              const prefixW1 = this.measureText(prefix1, seasonFontSize);
              const line1 = prefix1 + this.truncateText(names[1], seasonFontSize, maxSeasonWidth - prefixW1);
              seasonLines.push(line1);

              if (names.length >= 3) {
                let extra = '';
                if (names.length >= 5) {
                  extra = ` +${names.length - 3}`;
                } else if (names.length === 4) {
                  const suffixOne = ' +1';
                  const wSuffix = this.measureText(suffixOne, seasonFontSize);
                  const pairW = this.measureText(`${names[2]}, ${names[3]}`, seasonFontSize);
                  if (pairW + wSuffix > maxSeasonWidth * 0.86) {
                    extra = suffixOne;
                  }
                } else if (names.length === 3) {
                  const suffixOne = ' +1';
                  const wSuffix = this.measureText(suffixOne, seasonFontSize);
                  const w3 = this.measureText(names[2], seasonFontSize);
                  if (w3 + wSuffix > maxSeasonWidth * 0.86) {
                    extra = suffixOne;
                  }
                }
                const availW2 = extra ? maxSeasonWidth - this.measureText(extra, seasonFontSize) : maxSeasonWidth;
                if (names.length >= 4) {
                  const prefix2 = names[2] + ', ';
                  const prefixW2 = this.measureText(prefix2, seasonFontSize);
                  const line2 = prefix2 + this.truncateText(names[3], seasonFontSize, availW2 - prefixW2) + extra;
                  seasonLines.push(line2);
                } else {
                  const line2 = this.truncateText(names[2], seasonFontSize, availW2) + extra;
                  seasonLines.push(line2);
                }
              }
            }
          } else {
            const extraCount = names.length > 2 ? ` +${names.length - 2}` : '';
            const maxNames = Math.min(names.length, 2);
            let joined = names.slice(0, maxNames).join(', ');
            let seasonText = joined + extraCount;

            let seasonWidth = this.measureText(seasonText, seasonFontSize);
            if (seasonWidth > maxSeasonWidth && maxNames > 1) {
              const newExtra = ` +${names.length - 1}`;
              seasonText = names[0] + newExtra;
              seasonWidth = this.measureText(seasonText, seasonFontSize);
            }
            if (seasonWidth > maxSeasonWidth) {
              seasonText = this.truncateText(seasonText, seasonFontSize, maxSeasonWidth);
            }
            seasonLines.push(seasonText);
          }
        }

        seasonLines = seasonLines.map(line => this.truncateSeasonLegendLine(line, seasonFontSize, maxSeasonWidth));

        seasonLines.forEach((line, i) => {
          svg.append("text")
            .attr("x", nameX)
            .attr("y", nameY + pitchWidth * 0.030 * s + i * seasonLineH)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 400)
            .style("font-size", seasonFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "rgba(255,255,255,0.85)")
            .attr("class", "seasonText")
            .text(line);
        });

        const heroFontSize = pitchWidth * 0.025 * s;
        const heroY = crestY + crestSize + pitchWidth * 0.008 * s + (hasSecondSeasonLine ? pitchWidth * 0.010 * s : 0);

        if (isConceded) {
          const goalsBaseLabel = Dictionary[this.language]["table"]["roster"]["goals"];
          const xgBaseLabel = Dictionary[this.language]["playerScout"]["xG"];
          const concededSuffix = goalsLabel.replace(goalsBaseLabel, '').trim() || 'Conceded';

          let goalsPart = `${ goals } ${ goalsBaseLabel }`;
          if (this.showPenalties && this.totalPenaltyGoals > 0) goalsPart += ` (${this.totalPenaltyGoals} P)`;
          const xgPart = `${ xG.toFixed(xGDecimals) } ${ xgBaseLabel }`;
          let heroLine1 = `${ goalsPart }  ·  ${ xgPart }`;
          if (xGOnTargetOption) heroLine1 += `  ·  ${xGOnTarget.toFixed(xGDecimals)} xG OT`;

          svg.append("text")
            .attr("x", rectX + leftPad)
            .attr("y", heroY)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 700)
            .style("font-size", heroFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .attr("class", "playerXGandGoals")
            .text(heroLine1);

          const concededFontSize = pitchWidth * 0.014 * s;
          const goalsPartWidth = this.measureText(goalsPart, heroFontSize);
          const dotWidth = this.measureText('  ·  ', heroFontSize);

          svg.append("text")
            .attr("x", rectX + leftPad)
            .attr("y", heroY + pitchWidth * 0.027 * s)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 500)
            .style("font-size", concededFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .text(concededSuffix);

          svg.append("text")
            .attr("x", rectX + leftPad + goalsPartWidth + dotWidth - pitchWidth * 0.030 * s)
            .attr("y", heroY + pitchWidth * 0.027 * s)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 500)
            .style("font-size", concededFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .text(concededSuffix);
        } else {
          let goalsText = `${ goals } ${ goalsLabel }`;
          if (this.showPenalties && this.totalPenaltyGoals > 0) goalsText += ` (${this.totalPenaltyGoals} P)`;
          let heroLine1 = `${ goalsText }  ·  ${ xG.toFixed(xGDecimals) } ${ statName }`;
          if (xGOnTargetOption) heroLine1 += `  ·  ${xGOnTarget.toFixed(xGDecimals)} xG OT`;

          svg.append("text")
            .attr("x", rectX + leftPad)
            .attr("y", heroY)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 700)
            .style("font-size", heroFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .attr("class", "playerXGandGoals")
            .text(heroLine1);
        }

        if (finishingXGOTOption && shotsOnTarget > 0) {
          const finText = `${ (finishingXGOT / shotsOnTarget) > 1 ? '+' : ''}${ (((finishingXGOT / shotsOnTarget) - 1) * 100).toFixed(1) }% ${ Dictionary[this.language]["statsNaming"]["Finishing"] } xG OT`;
          const finY = isConceded ? heroY + pitchWidth * 0.042 * s : heroY + pitchWidth * 0.028 * s;
          svg.append("text")
            .attr("x", rectX + leftPad)
            .attr("y", finY)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 500)
            .style("font-size", finFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "rgba(255,255,255,0.9)")
            .text(finText);
        }

        // Vertical divider
        svg.append("line")
          .attr("x1", dividerX)
          .attr("y1", rectY + pitchWidth * 0.022 * s)
          .attr("x2", dividerX)
          .attr("y2", rectY + rectH - pitchWidth * 0.022 * s)
          .attr("stroke", "rgba(255,255,255,0.5)")
          .attr("stroke-width", 1);

        // Right side: stats and filters
        const rightX = dividerX + pitchWidth * 0.048 * s;
        const rightSectionW = rectX + rectW - dividerX;
        const labelFontSize = pitchWidth * 0.012 * s;
        const valueFontSize = pitchWidth * 0.019 * s;
        const rowH = pitchWidth * 0.042 * s;
        const midColX = dividerX + rightSectionW * 0.5;
        const totalRows = 3;
        const rightContentH = totalRows * rowH;
        const startY = rectY + (rectH - rightContentH) / 2;

        const gamesLabel = this.team
          ? Dictionary[this.language]["table"]["history"]["games"]
          : Dictionary[this.language]["table"]["history"]["games"] + ' / ' + Dictionary[this.language]["table"]["roster"]["minutes"];
        const gamesValue = (games === 1 && this.gameName)
          ? this.gameName
          : (this.team ? `${games}` : `${games} / ${minutes}`);
        const statsData = [
          { label: gamesLabel, value: gamesValue },
          { label: Dictionary[this.language]["game"]["stats"]["shots"], value: `${playerShots}` },
          { label: Dictionary[this.language]["finishingMap"]["chart"]["xGPerShot"], value: `${playerShots > 0 ? (xG / playerShots).toFixed(2) : '0.00'}` }
        ];

        statsData.forEach((stat, i) => {
          const y = startY + i * rowH;
          svg.append("text")
            .attr("x", rightX)
            .attr("y", y)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 400)
            .style("font-size", labelFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "rgba(255,255,255,0.7)")
            .text(stat.label);
          svg.append("text")
            .attr("x", rightX)
            .attr("y", y + pitchWidth * 0.015 * s)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 600)
            .style("font-size", valueFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .text(stat.value);
        });

        const typesLabel = Dictionary[this.language]["finishingMap"]["typesOfShot"];
        const strategyLabel = Dictionary[this.language]["finishingMap"]["shotStrategy"];
        const bodyPartLabel = Dictionary[this.language]["finishingMap"]["bodyPart"];
        const allLabel = Dictionary[this.language]["general"] && Dictionary[this.language]["general"]["all"] ? Dictionary[this.language]["general"]["all"] : 'All';
        const maxFilterWidth = rectX + rectW - midColX - pitchWidth * 0.034 * s;
        const filtersData = [
          { label: typesLabel, value: this.filterShotTypeText || allLabel },
          { label: strategyLabel, value: this.filterShotStrategyText || allLabel },
          { label: bodyPartLabel, value: this.filterBodyPartText || allLabel }
        ];

        filtersData.forEach((filter, i) => {
          const y = startY + i * rowH;
          svg.append("text")
            .attr("x", midColX)
            .attr("y", y)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 400)
            .style("font-size", labelFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "rgba(255,255,255,0.7)")
            .text(filter.label);

          const truncatedValue = this.truncateText(filter.value, valueFontSize, maxFilterWidth);
          svg.append("text")
            .attr("x", midColX)
            .attr("y", y + pitchWidth * 0.018 * s)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 600)
            .style("font-size", valueFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .text(truncatedValue);
        });

        const bottomInfoY = rectY + rectH - pitchWidth * 0.032 * s;
        const bottomInfoFontSize = pitchWidth * 0.011 * s;

        if (!this.showPenalties) {
          svg.append("text")
            .attr("x", rectX + pitchWidth * 0.038 * s)
            .attr("y", bottomInfoY)
            .attr("text-anchor", "start")
            .attr("dy", "0.8em")
            .attr("font-weight", 400)
            .style("font-size", bottomInfoFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .text('*' + Dictionary[this.language]["finishingMap"]["chart"]["penaltiesExcluded"]);
        }

        const minutesRangeText = this.filterMinutesText || this.filterMinutesRange;
        if (minutesRangeText) {
          svg.append("text")
            .attr("x", rectX + rectW - pitchWidth * 0.038 * s)
            .attr("y", bottomInfoY)
            .attr("text-anchor", "end")
            .attr("dy", "0.8em")
            .attr("font-weight", 400)
            .style("font-size", bottomInfoFontSize + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .text(minutesRangeText);
        }
      },
      renderBoxes(svg, svgWidth, pitchHeight, margin, shots, language = 'en') {
        let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';

        let x = d3.scale.linear().domain([39 , 61]).range([this.PITCH_WIDTH, 0]);
        let y = d3.scale.linear().domain([0, 92.5]).range([this.PITCH_HEIGHT - (this.PITCH_HEIGHT * 0.27), 0]);


        const shotLegend = this.$t("statsNaming.Shots").toLowerCase();

        const goalAreaLowRight = shots.filter(s => s.z_mouth <= 20 && s.y1 >= 45.2 && s.y1 <= 48.2).length;
        const goalAreaLowCenter = shots.filter(s => s.z_mouth <= 20 && s.y1 >= 48.2 && s.y1 <= 51.8).length;
        const goalAreaLowLeft = shots.filter(s => s.z_mouth <= 20 && s.y1 >= 51.8 && s.y1 <= 54.8).length;

        const goalAreaHighRight = shots.filter(s => s.z_mouth >= 20 && s.z_mouth <= 38 && s.y1 >= 45.2 && s.y1 <= 48.2).length;
        const goalAreaHighCenter = shots.filter(s => s.z_mouth >= 20 && s.z_mouth <= 38 && s.y1 >= 48.2 && s.y1 <= 51.8).length;
        const goalAreaHighLeft = shots.filter(s => s.z_mouth >= 20 && s.z_mouth <= 38 && s.y1 >= 51.8 && s.y1 <= 54.8).length;

        const closeAreaLeft = shots.filter(s => s.z_mouth <= 40 && s.y1 >= 55.8 && s.y1 <= 61).length;
        const closeAreaHighLeft = shots.filter(s => s.z_mouth >= 40 && s.z_mouth <= 60 && s.y1 >= 55.8 && s.y1 <= 61).length;
        const areaHighLeft = shots.filter(s => s.z_mouth >= 60 && s.z_mouth <= 92.5 && s.y1 >= 55.8 && s.y1 <= 61).length;

        const closeAreaRight = shots.filter(s => s.z_mouth <= 40 && s.y1 >= 39 && s.y1 <= 44.7).length;
        const closeAreaHighRight = shots.filter(s => s.z_mouth >= 40 && s.z_mouth <= 60 && s.y1 >= 39 && s.y1 <= 44.7).length;
        const areaHighRight = shots.filter(s => s.z_mouth >= 60 && s.z_mouth <= 92.5 && s.y1 >= 39 && s.y1 <= 44.7).length;

        const closeHighCenter = shots.filter(s => s.z_mouth >= 40 && s.z_mouth <= 60 && s.y1 >= 44.2 && s.y1 <= 55.8).length;
        const highCenter = shots.filter(s => s.z_mouth >= 60 && s.z_mouth <= 92.5 && s.y1 >= 44.2 && s.y1 <= 55.8).length;

        var shadowScale = d3.scale.linear().domain([0, shots.length]).range([0.1, 0.75]);

        if (goalAreaLowRight > 0.5) {
          let goalAreaLowRightX = x(48.2);
          let goalAreaLowRightY = y(20);
          let goalAreaLowRightWidth = x(44.7) - goalAreaLowRightX;
          let goalAreaLowRightHeight = y(0) - goalAreaLowRightY;
          this.drawRectangle(svg, goalAreaLowRightX, goalAreaLowRightY, goalAreaLowRightWidth, goalAreaLowRightHeight, "red", shadowScale(goalAreaLowRight), "gameLegend1");
          this.writeText(svg, goalAreaLowRightX + (goalAreaLowRightWidth / 2),goalAreaLowRightY + (goalAreaLowRightHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(goalAreaLowRight / shots.length * 100) } %`,textColor);
          this.writeText(svg, goalAreaLowRightX + (goalAreaLowRightWidth / 2),goalAreaLowRightY + (goalAreaLowRightHeight / 2) + 7, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(goalAreaLowRight) } ${ shotLegend })`,textColor);
        }
        if (goalAreaLowCenter > 0.5) {
          let goalAreaLowCenterX = x(51.7);
          let goalAreaLowCenterY = y(20);
          let goalAreaLowCenterWidth = x(48.3) - goalAreaLowCenterX;
          let goalAreaLowCenterHeight = y(0) - goalAreaLowCenterY;
          this.drawRectangle(svg, goalAreaLowCenterX, goalAreaLowCenterY, goalAreaLowCenterWidth, goalAreaLowCenterHeight, "red", shadowScale(goalAreaLowCenter), "gameLegend1");
          this.writeText(svg, goalAreaLowCenterX + (goalAreaLowCenterWidth / 2),goalAreaLowCenterY + (goalAreaLowCenterHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(goalAreaLowCenter / shots.length * 100) } %`,textColor);
          this.writeText(svg, goalAreaLowCenterX + (goalAreaLowCenterWidth / 2),goalAreaLowCenterY + (goalAreaLowCenterHeight / 2) + 7, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(goalAreaLowCenter) } ${ shotLegend })`,textColor);
        }
        if (goalAreaLowLeft > 0.5) {
          let goalAreaLowLeftX = x(55.25);
          let goalAreaLowLeftY = y(20);
          let goalAreaLowLeftWidth = x(51.8) - goalAreaLowLeftX;
          let goalAreaLowLeftHeight = y(0) - goalAreaLowLeftY;
          this.drawRectangle(svg, goalAreaLowLeftX, goalAreaLowLeftY, goalAreaLowLeftWidth, goalAreaLowLeftHeight, "red", shadowScale(goalAreaLowLeft), "gameLegend1");
          this.writeText(svg, goalAreaLowLeftX + (goalAreaLowLeftWidth / 2),goalAreaLowLeftY + (goalAreaLowLeftHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(goalAreaLowLeft / shots.length * 100) } %`,textColor);
          this.writeText(svg, goalAreaLowLeftX + (goalAreaLowLeftWidth / 2),goalAreaLowLeftY + (goalAreaLowLeftHeight / 2) + 7, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(goalAreaLowLeft) } ${ shotLegend })`,textColor);
        }

        if (goalAreaHighRight > 0.5) {
          let goalAreaHighRightX = x(48.2);
          let goalAreaHighRightY = y(40);
          let goalAreaHighRightWidth = x(44.7) - goalAreaHighRightX;
          let goalAreaHighRightHeight = y(21) - goalAreaHighRightY;
          this.drawRectangle(svg, goalAreaHighRightX, goalAreaHighRightY, goalAreaHighRightWidth, goalAreaHighRightHeight, "red", shadowScale(goalAreaHighRight), "gameLegend1");
          this.writeText(svg, goalAreaHighRightX + (goalAreaHighRightWidth / 2),goalAreaHighRightY + (goalAreaHighRightHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(goalAreaHighRight / shots.length * 100) } %`,textColor);
          this.writeText(svg, goalAreaHighRightX + (goalAreaHighRightWidth / 2),goalAreaHighRightY + (goalAreaHighRightHeight / 2) + 6, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(goalAreaHighRight) } ${ shotLegend })`,textColor);
        }
        if (goalAreaHighCenter > 0.5) {
          let goalAreaHighCenterX = x(51.7);
          let goalAreaHighCenterY = y(40);
          let goalAreaHighCenterWidth = x(48.3) - goalAreaHighCenterX;
          let goalAreaHighCenterHeight = y(21) - goalAreaHighCenterY;
          this.drawRectangle(svg, goalAreaHighCenterX, goalAreaHighCenterY, goalAreaHighCenterWidth, goalAreaHighCenterHeight, "red", shadowScale(goalAreaHighCenter), "gameLegend1");
          this.writeText(svg, goalAreaHighCenterX + (goalAreaHighCenterWidth / 2),goalAreaHighCenterY + (goalAreaHighCenterHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(goalAreaHighCenter / shots.length * 100) } %`,textColor);
          this.writeText(svg, goalAreaHighCenterX + (goalAreaHighCenterWidth / 2),goalAreaHighCenterY + (goalAreaHighCenterHeight / 2) + 6, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(goalAreaHighCenter) } ${ shotLegend })`,textColor);
        }
        if (goalAreaHighLeft > 0.5) {
          let goalAreaHighLeftX = x(55.25);
          let goalAreaHighLeftY = y(40);
          let goalAreaHighLeftWidth = x(51.8) - goalAreaHighLeftX;
          let goalAreaHighLeftHeight = y(21) - goalAreaHighLeftY;
          this.drawRectangle(svg, goalAreaHighLeftX, goalAreaHighLeftY, goalAreaHighLeftWidth, goalAreaHighLeftHeight, "red", shadowScale(goalAreaHighLeft), "gameLegend1");
          this.writeText(svg, goalAreaHighLeftX + (goalAreaHighLeftWidth / 2),goalAreaHighLeftY + (goalAreaHighLeftHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(goalAreaHighLeft / shots.length * 100) } %`,textColor);
          this.writeText(svg, goalAreaHighLeftX + (goalAreaHighLeftWidth / 2),goalAreaHighLeftY + (goalAreaHighLeftHeight / 2) + 6, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(goalAreaHighLeft) } ${ shotLegend })`,textColor);
        }

        if (closeAreaLeft > 0.5) {
          let closeAreaLeftX = x(60.9);
          let closeAreaLeftY = y(40);
          let closeAreaLeftWidth = x(55.4) - closeAreaLeftX;
          let closeAreaLeftHeight = y(0) - closeAreaLeftY;
          this.drawRectangle(svg, closeAreaLeftX, closeAreaLeftY, closeAreaLeftWidth, closeAreaLeftHeight, "red", shadowScale(closeAreaLeft), "gameLegend1");
          this.writeText(svg, closeAreaLeftX + (closeAreaLeftWidth / 2),closeAreaLeftY + (closeAreaLeftHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(closeAreaLeft / shots.length * 100) } %`,textColor);
          this.writeText(svg, closeAreaLeftX + (closeAreaLeftWidth / 2),closeAreaLeftY + (closeAreaLeftHeight / 2) + 6, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(closeAreaLeft) } ${ shotLegend })`,textColor);
        }
        if (closeAreaHighLeft > 0.5) {
          let closeAreaHighLeftX = x(60.9);
          let closeAreaHighLeftY = y(60);
          let closeAreaHighLeftWidth = x(55.4) - closeAreaHighLeftX;
          let closeAreaHighLeftHeight = y(41) - closeAreaHighLeftY;
          this.drawRectangle(svg, closeAreaHighLeftX, closeAreaHighLeftY, closeAreaHighLeftWidth, closeAreaHighLeftHeight, "red", shadowScale(closeAreaHighLeft), "gameLegend1");
          this.writeText(svg, closeAreaHighLeftX + (closeAreaHighLeftWidth / 2),closeAreaHighLeftY + (closeAreaHighLeftHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(closeAreaHighLeft / shots.length * 100) } %`,textColor);
          this.writeText(svg, closeAreaHighLeftX + (closeAreaHighLeftWidth / 2),closeAreaHighLeftY + (closeAreaHighLeftHeight / 2) + 2, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(closeAreaHighLeft) } ${ shotLegend })`,textColor);
        }
        if (areaHighLeft > 0.5) {
          let areaHighLeftX = x(60.9);
          let areaHighLeftY = y(91.75);
          let areaHighLeftWidth = x(55.4) - areaHighLeftX;
          let areaHighLeftHeight = y(61) - areaHighLeftY;
          this.drawRectangle(svg, areaHighLeftX, areaHighLeftY, areaHighLeftWidth, areaHighLeftHeight, "red", shadowScale(areaHighLeft), "gameLegend1");
          this.writeText(svg, areaHighLeftX + (areaHighLeftWidth / 2),areaHighLeftY + (areaHighLeftHeight / 2) - 7, "middle", (0.018 * svgWidth) + "px", `${ Math.round(areaHighLeft / shots.length * 100) } %`,textColor);
          this.writeText(svg, areaHighLeftX + (areaHighLeftWidth / 2),areaHighLeftY + (areaHighLeftHeight / 2) + 6, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(areaHighLeft) } ${ shotLegend })`,textColor);
        }

        if (closeAreaRight > 0.5) {
          let closeAreaRightX = x(44.55);
          let closeAreaRightY = y(40);
          let closeAreaRightWidth = x(39.1) - closeAreaRightX;
          let closeAreaRightHeight = y(0) - closeAreaRightY;
          this.drawRectangle(svg, closeAreaRightX, closeAreaRightY, closeAreaRightWidth, closeAreaRightHeight, "red", shadowScale(closeAreaRight), "gameLegend1");
          this.writeText(svg, closeAreaRightX + (closeAreaRightWidth / 2),closeAreaRightY + (closeAreaRightHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(closeAreaRight / shots.length * 100) } %`,textColor);
          this.writeText(svg, closeAreaRightX + (closeAreaRightWidth / 2),closeAreaRightY + (closeAreaRightHeight / 2) + 6, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(closeAreaRight) } ${ shotLegend })`,textColor);
        }
        if (closeAreaHighRight > 0.5) {
          let closeAreaHighRightX = x(44.55);
          let closeAreaHighRightY = y(60);
          let closeAreaHighRightWidth = x(39.1) - closeAreaHighRightX;
          let closeAreaHighRightHeight = y(41) - closeAreaHighRightY;
          this.drawRectangle(svg, closeAreaHighRightX, closeAreaHighRightY, closeAreaHighRightWidth, closeAreaHighRightHeight, "red", shadowScale(closeAreaHighRight), "gameLegend1");
          this.writeText(svg, closeAreaHighRightX + (closeAreaHighRightWidth / 2),closeAreaHighRightY + (closeAreaHighRightHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(closeAreaHighRight / shots.length * 100) } %`,textColor);
          this.writeText(svg, closeAreaHighRightX + (closeAreaHighRightWidth / 2),closeAreaHighRightY + (closeAreaHighRightHeight / 2) + 2, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(closeAreaHighRight) } ${ shotLegend })`,textColor);
        }
        if (areaHighRight > 0.5) {
          let areaHighRightX = x(44.55);
          let areaHighRightY = y(91.75);
          let areaHighRightWidth = x(39.1) - areaHighRightX;
          let areaHighRightHeight = y(61) - areaHighRightY;
          this.drawRectangle(svg, areaHighRightX, areaHighRightY, areaHighRightWidth, areaHighRightHeight, "red", shadowScale(areaHighRight), "gameLegend1");
          this.writeText(svg, areaHighRightX + (areaHighRightWidth / 2),areaHighRightY + (areaHighRightHeight / 2) - 7, "middle", (0.018 * svgWidth) + "px", `${ Math.round(areaHighRight / shots.length * 100) } %`,textColor);
          this.writeText(svg, areaHighRightX + (areaHighRightWidth / 2),areaHighRightY + (areaHighRightHeight / 2) + 6, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(areaHighRight) } ${ shotLegend })`,textColor);
        }

        if (closeHighCenter > 0.5) {
          let closeHighCenterX = x(55.3);
          let closeHighCenterY = y(60);
          let closeHighCenterWidth = x(44.65) - closeHighCenterX;
          let closeHighCenterHeight = y(41.5) - closeHighCenterY;
          this.drawRectangle(svg, closeHighCenterX, closeHighCenterY, closeHighCenterWidth, closeHighCenterHeight, "red", shadowScale(closeHighCenter), "gameLegend1");
          this.writeText(svg, closeHighCenterX + (closeHighCenterWidth / 2),closeHighCenterY + (closeHighCenterHeight / 2) - 14, "middle", (0.018 * svgWidth) + "px", `${ Math.round(closeHighCenter / shots.length * 100) } %`,textColor);
          this.writeText(svg, closeHighCenterX + (closeHighCenterWidth / 2),closeHighCenterY + (closeHighCenterHeight / 2) + 2, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(closeHighCenter) } ${ shotLegend })`,textColor);
        }
        if (highCenter > 0.5) {
          let highCenterX = x(55.3);
          let highCenterY = y(91.75);
          let highCenterWidth = x(44.65) - highCenterX;
          let highCenterHeight = y(61) - highCenterY;
          this.drawRectangle(svg, highCenterX, highCenterY, highCenterWidth, highCenterHeight, "red", shadowScale(highCenter), "gameLegend1");
          this.writeText(svg, highCenterX + (highCenterWidth / 2),highCenterY + (highCenterHeight / 2) - 7, "middle", (0.018 * svgWidth) + "px", `${ Math.round(highCenter / shots.length * 100) } %`,textColor);
          this.writeText(svg, highCenterX + (highCenterWidth / 2),highCenterY + (highCenterHeight / 2) + 6, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(highCenter) } ${ shotLegend })`,textColor);
        }
      },
      onResize(){
        let elto =document.getElementById(this.rootElementId);
        if(elto){
          this.$emit("sizeChange", {height:elto.clientHeight,width:elto.clientWidth});
        }
      }
    },
    destroyed() {
      delete this.observer;
      d3.select(`#${this.rootElementId}`).select("svg").remove();
    },
  }

</script>



// WEBPACK FOOTER //
// src/views/base/XGGoalChart.vue